<template>
  <div class="flex flex-col">
    <h2>Citas con especialista</h2>

    <custom-search
      class="mt-6 w-full sm:w-[400px]"
      placeholder="Buscar por Cliente ó Mascota"
      v-model="searchText"
      @confirm="search"
      @clear="search"
    />

    <div class="flex flex-col lg:flex-row gap-4 lg:gap-6 mt-6 items-start">
      <status-side-nav :options="statusNavOptions" @select="setStatusFilter" />

      <div class="flex-1 min-w-0 w-full">
    <div class="card">
      <table class="table-auto text-sm mt-2 bg-white w-full">
        <thead class="text-left">
          <tr class="bg-primary-100">
            <th class="px-4 py-3 rounded-l-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">ID</th>
            <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Cliente</th>
            <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Mascota</th>
            <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Motivo</th>
            <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Estado</th>
            <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Fecha</th>
            <th class="px-4 py-3 rounded-r-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="request in appointmentRequests"
            :key="request.id"
            class="table-row cursor-pointer"
            @click="goToDetail(request.id)"
          >
            <td class="table-cell">{{ request.id }}</td>
            <td class="table-cell">
              <router-link
                :to="{ name: 'clientsDetail', params: { id: request.client.id } }"
                class="underline system-button"
                @click.stop
              >{{ request.client.name }} {{ request.client.last_name }}</router-link>
            </td>
            <td class="table-cell">
              <router-link
                v-if="request.pet"
                :to="{ name: 'petsDetail', params: { id: request.pet.id } }"
                class="underline system-button"
                @click.stop
              >{{ request.pet.name }}</router-link>
              <span v-else class="text-content-300">—</span>
            </td>
            <td class="table-cell max-w-xs truncate" :title="request.reason">
              {{ request.reason }}
            </td>
            <td class="table-cell">
              <span :class="['badge', statusBadgeClass(request.status)]">
                <span class="badge-dot"></span> {{ statusLabel(request.status) }}
              </span>
            </td>
            <td class="table-cell">
              {{ formatDatetime(request.created_at) }}
              <div
                v-if="request.status === 'scheduled' && request.scheduled_for"
                class="text-xs text-content-300 mt-0.5"
              >
                Cita: {{ formatDatetime(request.scheduled_for) }}
              </div>
            </td>
            <td class="table-cell">
              <router-link
                :to="{ name: 'appointmentRequestsDetail', params: { id: request.id } }"
                class="secondary-button"
                @click.stop
              >Detalle</router-link>
            </td>
          </tr>
          <tr v-if="!loading && !appointmentRequests.length">
            <td colspan="7" class="text-center p-4 text-content-300">
              No hay solicitudes de citas que mostrar.
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="loading" class="flex w-full h-full items-center">
        <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
      </div>
    </div>

    <pagination
      :current-page="currentPage"
      :total-pages="totalPages"
      @page-change="onPageChange"
    />
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import Pagination from "@/components/ui/Pagination.vue";
import StatusSideNav from "@/components/ui/StatusSideNav";
import { formatDatetime } from "@/utils/dateFormatter";

const STATUS_LABELS = {
  pending: "Pendiente",
  needs_info: "Esperando cliente",
  approved: "Aprobada",
  scheduled: "Agendada",
  completed: "Finalizada",
  no_show: "No asistió",
  canceled: "Cancelada",
  rejected: "Rechazada",
};

const STATUS_BADGE_CLASSES = {
  pending: "badge-warning",
  needs_info: "badge-info",
  approved: "badge-success",
  scheduled: "badge-info",
  completed: "badge-success",
  no_show: "badge-warning",
  canceled: "badge-error",
  rejected: "badge-error",
};

export default {
  name: "Appointment Requests",

  components: {
    Pagination,
    StatusSideNav,
  },

  computed: {
    statusNavOptions() {
      return [
        { value: "", label: "Todas", icon: "📋", activeClass: "text-primary-600" },
        { value: "pending", label: "Pendientes", icon: "🕐", activeClass: "text-tertiary-500" },
        { value: "needs_info", label: "Esperando cliente", icon: "💬", activeClass: "text-tertiary-500" },
        { value: "approved", label: "Aprobadas", icon: "👍", activeClass: "text-success-500" },
        { value: "scheduled", label: "Agendadas", icon: "📅", activeClass: "text-quaternary-500" },
        { value: "completed", label: "Finalizadas", icon: "✅", activeClass: "text-success-500" },
        { value: "no_show", label: "No asistió", icon: "🚫", activeClass: "text-error-500" },
        { value: "canceled", label: "Canceladas", icon: "❌", activeClass: "text-error-500" },
        { value: "rejected", label: "Rechazadas", icon: "⛔", activeClass: "text-error-600" },
      ].map((option) => ({
        ...option,
        active: this.statusFilter === option.value,
      }));
    },
  },

  data() {
    return {
      appointmentRequests: [],
      loading: false,
      statusFilter: "",
      searchText: "",
      currentPage: 1,
      totalPages: 0,
    };
  },

  created() {
    this.$store.commit("setSelectedTab", "appointmentRequests");
    this.loadAppointmentRequests();
  },

  methods: {
    formatDatetime,

    statusLabel(status) {
      return STATUS_LABELS[status] || status;
    },

    statusBadgeClass(status) {
      return STATUS_BADGE_CLASSES[status] || "badge-neutral";
    },

    async loadAppointmentRequests() {
      this.loading = true;

      try {
        const response = await api.appointmentRequests.getAllWithQueryParams([
          { name: "status", value: this.statusFilter },
          { name: "page", value: this.currentPage },
          { name: "search", value: this.searchText },
        ]);
        this.appointmentRequests = response.data || [];
        this.currentPage = response.current_page || 1;
        this.totalPages = response.last_page || 0;
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    search() {
      this.currentPage = 1;
      this.loadAppointmentRequests();
    },

    setStatusFilter(value) {
      this.statusFilter = value;
      this.search();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadAppointmentRequests();
    },

    goToDetail(id) {
      this.$router.push({ name: "appointmentRequestsDetail", params: { id } });
    },
  },
};
</script>
